import { fetchCatalogAdIfNeeded } from '@/redux/modules/catalog/ad/catalogAd.actions';
import { CatalogAds } from '@/redux/modules/catalog/ad/catalogAd.types';
import { fetchCategoryRelatedSearches } from '@/redux/modules/categoryRelatedSearches';
import { loadChairishSimilarItems } from '@/redux/modules/chairishSimilarItems/chairishSimilarItems.actions';
import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import { getItemSummarySellerId, getItemSummaryTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import { fetchItemFacetsIfNeeded, getItemFacets } from '@/redux/modules/itemFacets';
import { getSearchPageSize } from '@/redux/modules/pagination';
import { fetchRelatedSearchesForSearchPage } from '@/redux/modules/search/relatedSearches/relatedSearches.actions';
import { setIsZeroResults, submitSearch } from '@/redux/modules/search/search.actions';
import { getSearchedItemIds, getSearchUiState } from '@/redux/modules/search/search.selectors';
import { getSearchFacets } from '@/redux/modules/searchFacetAggregates.selectors';
import { getSEOSearchPageBreadcrumbs } from '@/redux/modules/searchPromoted.selectors';
import { fetchSecondarySearchSuggestions } from '@/redux/modules/searchSuggestions/searchSuggestions.actions';
import { getSecondarySearchSuggestions } from '@/redux/modules/searchSuggestions/searchSuggestions.selectors';
import { fetchSellersIfNeeded } from '@/redux/modules/seller';
import { fetchSimilarItemsIfNeeded, getSimilarItemIds } from '@/redux/modules/similarItems';
import { DataLoaderParams } from '@/types/DataLoaderParams';
import FacetTypes from '@/types/search/enums/facetTypes';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { SearchPageName } from '@/types/search/searchQuery';
import { FilterAction } from '@/types/SearchFacet';
import { AmplitudeExperiments, AmplitudeVariants } from '@/utils/amplitude/experiments/AmplitudeExperiments';
import cleanSearchTerms from '@/utils/cleanSearchTerms';
import { getFacetIds } from '@/utils/ItemPageUtils';
import {
    getCategoryIdsFromQueryParams,
    getKeywordFromQueryParams,
    isArchivedSearch,
    parseQueryParams,
} from '@/utils/queryParams';
import { buildSearchQuery, isBuyNowQuery } from '@/utils/search/searchQuery';
import { parseSearchSeoPath, resolveSearchSeoPathSubjectToBrowseFacetIds } from '@/utils/search/searchSeoPath';
import { getIsCollectionsPage, getIsSEOSearchPage, getIsSEOSearchPageWithLocation } from '@/utils/urls';

export default ({ amplitudeExperimentFlags, location, routerState, store }: DataLoaderParams) => {
    const { dispatch, getState } = store;
    const queryParams = parseQueryParams(location.search);
    const { pathname } = location;

    const archivedSearch = isArchivedSearch(queryParams, pathname);

    // Experiment: SEAR-865 Chairish Recommendations on Zero Results Page
    const chairishRecommendationsOnZeroResultsPageVariant =
        amplitudeExperimentFlags[AmplitudeExperiments.ChairishRecommendationsOnZeroResultsPage]?.value;
    const hasChairishRecommendationsOnZeroResultsPage =
        chairishRecommendationsOnZeroResultsPageVariant ===
        AmplitudeVariants.ChairishRecommendationsOnZeroResultsPage.Treatment;

    // Experiment: SEAR-998 Popularity Score Re-Ranking
    const popularityScoreReRankingTreatment =
        amplitudeExperimentFlags[AmplitudeExperiments.PopularityScoreReRanking]?.value;

    let popularityScoreReRankingVersion: string | undefined;
    if (popularityScoreReRankingTreatment === AmplitudeVariants.PopularityScoreReRanking.AddPopularityKeepingBidCount) {
        popularityScoreReRankingVersion = 'v2';
    } else if (
        popularityScoreReRankingTreatment === AmplitudeVariants.PopularityScoreReRanking.AddPopularityRemovingBidCount
    ) {
        popularityScoreReRankingVersion = 'v3';
    }

    // Experiment: SEAR-1387 LTR MVP V1 Search Ranking
    const LEARNING_TO_RANK_MODEL_VERSION = 'v2';
    const hasLearningToRankSearchReRankingTreatment =
        amplitudeExperimentFlags[AmplitudeExperiments.LearningToRankSearchReRanking]?.value ===
        AmplitudeVariants.LearningToRankSearchReRanking.Treatment;

    const nonFilterActions: FilterAction[] = [
        FilterAction.PageChanged,
        FilterAction.PageSizeChanged,
        FilterAction.SortByChanged,
        FilterAction.ViewPriceResults,
        FilterAction.ViewUpcomingItems,
    ];
    const isSSR = !routerState;
    const isFilterRefinement =
        Boolean(routerState?.filterAction) && !nonFilterActions.includes(routerState?.filterAction);
    const needsAggs = isSSR || isFilterRefinement;

    return (async () => {
        const currentState = getState();

        const pageSize = getSearchPageSize(currentState);
        let browseFacetOptionIds = resolveSearchSeoPathSubjectToBrowseFacetIds(pathname, getSearchFacets(currentState));
        const buildSearchQueryForPage = (qp: typeof queryParams) =>
            buildSearchQuery({
                browseFacetOptionIds,
                pathname,
                queryParams: qp,
                searchPageSize: pageSize,
            });

        let searchQuery = buildSearchQueryForPage(queryParams);

        const isBIN = isBuyNowQuery(searchQuery);
        searchQuery.buyNow = !archivedSearch && isBIN;
        const { searchTerm } = searchQuery;

        let pageName = archivedSearch ? SearchPageName.SOLD_SEARCH : SearchPageName.SEARCH;

        const isSEOSearchPage = getIsSEOSearchPage(pathname) || getIsSEOSearchPageWithLocation(pathname);
        if (isSEOSearchPage) {
            pageName = SearchPageName.SEO_SEARCH;
        } else if (getIsCollectionsPage(pathname)) {
            pageName = SearchPageName.COLLECTIONS;
        }

        const keywords = getKeywordFromQueryParams(queryParams);

        const promises = [
            dispatch(
                submitSearch({
                    ...(hasLearningToRankSearchReRankingTreatment && {
                        learningToRankModelVersion: LEARNING_TO_RANK_MODEL_VERSION,
                    }),
                    pageName,
                    popularityScoreReRankingVersion,
                    routerState,
                    searchQuery,
                    skipAggs: !needsAggs,
                })
            ),
            dispatch(
                fetchSecondarySearchSuggestions({
                    term: cleanSearchTerms(searchTerm),
                })
            ),
            // fetch the search results page banner ad - with force in order to fetch a new ad each time the page changes
            dispatch(
                fetchCatalogAdIfNeeded({
                    force: true,
                    pageName: CatalogAds.SearchPageBanner,
                })
            ),
        ];

        await Promise.all(promises);

        const subjectTail = parseSearchSeoPath(pathname)?.subjectValueSlug;
        if (subjectTail && !browseFacetOptionIds?.[FacetTypes.ART_SUBJECT_ID]?.length && !/^\d+$/.test(subjectTail)) {
            const retryBrowse = resolveSearchSeoPathSubjectToBrowseFacetIds(pathname, getSearchFacets(getState()));
            if (retryBrowse?.[FacetTypes.ART_SUBJECT_ID]?.length) {
                browseFacetOptionIds = retryBrowse;
                searchQuery = buildSearchQueryForPage(queryParams);
                await dispatch(
                    submitSearch({
                        ...(hasLearningToRankSearchReRankingTreatment && {
                            learningToRankModelVersion: LEARNING_TO_RANK_MODEL_VERSION,
                        }),
                        pageName,
                        popularityScoreReRankingVersion,
                        routerState,
                        searchQuery,
                        skipAggs: !needsAggs,
                    })
                );
            }
        }

        const searchState = getState();
        const itemIds = getSearchedItemIds(searchState);
        const { totalFound, totalSoldItems } = getSearchUiState(searchState);

        const shouldPreloadSoldItems = !archivedSearch && totalFound === 0 && totalSoldItems > 0;

        if (itemIds.length) {
            // If we have search results do all the stuff to get the data for them.
            const { houses } = getSecondarySearchSuggestions(searchState);

            const promises: Promise<any>[] = [
                dispatch(
                    fetchCatalogAdIfNeeded({
                        categoryIds: getCategoryIdsFromQueryParams(queryParams),
                        keywords: `${keywords}`,
                        pageName,
                    })
                ),
                // Fetch item summaries for search results to enable SSR
                dispatch(
                    fetchItemSummariesIfNeeded({
                        identifier: 'SEARCH-PAGE-RESULTS',
                        itemIds,
                    })
                ),
            ];

            if (houses?.[0]?.houseId) {
                promises.push(dispatch(fetchSellersIfNeeded([houses[0]?.houseId])));
            }

            if (isSEOSearchPage) {
                const searchPageBreadcrumbs = getSEOSearchPageBreadcrumbs(searchState);
                if (searchPageBreadcrumbs.length > 0) {
                    const relatedCategoryId = searchPageBreadcrumbs[searchPageBreadcrumbs.length - 1].id;
                    promises.push(dispatch(fetchCategoryRelatedSearches(itemIds, relatedCategoryId)));
                }
            }

            promises.push(dispatch(fetchRelatedSearchesForSearchPage(searchTerm)));

            return Promise.all(promises);
        } else {
            // If we get no results back, we'll get the data for our zero results page
            if (shouldPreloadSoldItems) {
                await dispatch(setIsZeroResults(true));
                await dispatch(
                    submitSearch({
                        pageName,
                        routerState,
                        searchQuery: buildSearchQuery({
                            browseFacetOptionIds,
                            defaultSortOrder: SearchSortAndDirection.TimeNewest,
                            pathname,
                            queryParams: {
                                ...queryParams,
                                status: 'archive',
                            },
                            searchPageSize: pageSize,
                            similarItems: true,
                        }),
                        skipAggs: true,
                    })
                );
            }

            const { soldItemIds = [] } = getSearchUiState(searchState);
            const promises = [
                dispatch(
                    fetchItemSummariesIfNeeded({
                        identifier: 'SEARCH-PAGE-SOLD',
                        itemIds: soldItemIds,
                        similarItems: true,
                    })
                ),
                dispatch(fetchItemFacetsIfNeeded([soldItemIds[0]])),
            ];

            return Promise.all(promises).then(() => {
                const state = getState();
                const searchState = getSearchUiState(state);

                if (searchState?.itemIds?.length > 0) {
                    const itemId = searchState.itemIds[0];
                    const sellerId = getItemSummarySellerId(state, itemId);
                    const title = getItemSummaryTitle(state, itemId);
                    const facets = getItemFacets(state, itemId);
                    const categoryIds = getFacetIds(facets);

                    const zeroResultsPromises: Promise<unknown>[] = [
                        dispatch(
                            fetchSimilarItemsIfNeeded({
                                categoryIds,
                                itemId,
                                sellerId,
                                title,
                            })
                        ),
                    ];

                    if (hasChairishRecommendationsOnZeroResultsPage) {
                        zeroResultsPromises.push(dispatch(loadChairishSimilarItems({ itemId })));
                    }

                    return Promise.all(zeroResultsPromises).then(() => {
                        const state = getState();
                        const similarItemIds = getSimilarItemIds(state, itemId);
                        return dispatch(
                            fetchItemSummariesIfNeeded({
                                identifier: 'SEARCH-PAGE-SIMILAR',
                                itemIds: similarItemIds,
                            })
                        );
                    });
                }
            });
        }
    })();
};
